import { useState, useMemo } from 'react';
import { Calculator } from '@phosphor-icons/react';
import { useLanguage } from '@/context/LanguageContext';
import useDocumentMeta from '@/hooks/useDocumentMeta';
import { TOOL_SEO, toolPath } from '@/lib/toolRoutes';
import ToolLayout, { Card, Field, Input, Select, UpsellBanner } from './_ToolLayout';

const CURRENCIES = [['TRY', '₺'], ['USD', '$'], ['EUR', '€'], ['GBP', '£']];

// Default commission rates by platform (rough averages; user can override)
const PLATFORMS = [
  { id: 'trendyol', label: 'Trendyol', commission: 18, ship: 30, payout: 14 },
  { id: 'hepsiburada', label: 'Hepsiburada', commission: 17, ship: 30, payout: 14 },
  { id: 'amazon_tr', label: 'Amazon TR', commission: 15, ship: 25, payout: 7 },
  { id: 'amazon_us', label: 'Amazon US', commission: 15, ship: 40, payout: 14 },
  { id: 'etsy', label: 'Etsy', commission: 6.5, ship: 0, payout: 7 },
  { id: 'shopify', label: 'Shopify (Direct)', commission: 2.9, ship: 0, payout: 2 },
];

export default function PricingEngineTool() {
  const { t, language } = useLanguage();
  const seo = TOOL_SEO.fiyat[language] || TOOL_SEO.fiyat.tr;
  useDocumentMeta({
    title: seo.title,
    description: seo.description,
    canonical: toolPath('fiyat', language),
    alternates: { tr: toolPath('fiyat', 'tr'), en: toolPath('fiyat', 'en'), 'x-default': toolPath('fiyat', 'tr') },
    ogKind: 'tool',
  });
  const [currency, setCurrency] = useState('TRY');
  const [productCost, setProductCost] = useState(100);
  const [shippingCost, setShippingCost] = useState(15);
  const [packagingCost, setPackagingCost] = useState(5);
  const [marketingCost, setMarketingCost] = useState(10);
  const [otherCost, setOtherCost] = useState(0);
  const [targetMargin, setTargetMargin] = useState(30);
  const [vatRate, setVatRate] = useState(20);
  const [platforms, setPlatforms] = useState(PLATFORMS.map((p) => ({ ...p, enabled: true })));

  const sym = CURRENCIES.find(([c]) => c === currency)?.[1] || '';
  const totalCost = useMemo(() => Number(productCost) + Number(shippingCost) + Number(packagingCost) + Number(marketingCost) + Number(otherCost), [productCost, shippingCost, packagingCost, marketingCost, otherCost]);

  const results = useMemo(() => {
    const m = Number(targetMargin) / 100;
    const vat = Number(vatRate) / 100;
    return platforms.filter((p) => p.enabled).map((p) => {
      const com = Number(p.commission) / 100;
      // Selling price (incl. VAT) so that:
      // (price / (1+vat)) * (1 - com) - totalCost = totalCost * m
      // => price = totalCost * (1 + m) / (1 - com) * (1 + vat)
      const denom = 1 - com;
      if (denom <= 0) return { ...p, sellPrice: null, netProfit: null, margin: null };
      const sellPrice = (totalCost * (1 + m) / denom) * (1 + vat);
      const netRevenue = (sellPrice / (1 + vat)) * (1 - com);
      const netProfit = netRevenue - totalCost;
      const realMargin = (netProfit / totalCost) * 100;
      return { ...p, sellPrice, netRevenue, netProfit, realMargin };
    });
  }, [platforms, targetMargin, vatRate, totalCost]);

  const updatePlatform = (i, k, v) => setPlatforms(platforms.map((p, idx) => idx === i ? { ...p, [k]: v } : p));
  const fmt = (n) => n == null ? '—' : `${n.toFixed(2)} ${sym}`;

  return (
    <ToolLayout
      testId="pricing-tool"
      tourId="pricing"
      title={t('tools.fiyat.title')}
      subtitle={t('tools.fiyat.subtitle')}
    >
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Inputs */}
        <div className="space-y-6">
          <Card>
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><Calculator size={20} weight="bold" className="text-[#3CD9B8]" /> {t('tools.fiyat.section.costs')}</h3>
            <Field label={t('tools.fiyat.currency')}>
              <Select data-testid="pr-currency" value={currency} onChange={(e) => setCurrency(e.target.value)}>
                {CURRENCIES.map(([c, s]) => <option key={c} value={c} className="bg-[#0F1B36]">{c} ({s})</option>)}
              </Select>
            </Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label={t('tools.fiyat.product_cost')}><Input type="number" data-testid="pr-product-cost" value={productCost} onChange={(e) => setProductCost(e.target.value)} /></Field>
              <Field label={t('tools.fiyat.shipping_cost')}><Input type="number" data-testid="pr-shipping-cost" value={shippingCost} onChange={(e) => setShippingCost(e.target.value)} /></Field>
              <Field label={t('tools.fiyat.packaging_cost')}><Input type="number" data-testid="pr-packaging-cost" value={packagingCost} onChange={(e) => setPackagingCost(e.target.value)} /></Field>
              <Field label={t('tools.fiyat.marketing_cost')}><Input type="number" data-testid="pr-marketing-cost" value={marketingCost} onChange={(e) => setMarketingCost(e.target.value)} /></Field>
              <Field label={t('tools.fiyat.other_cost')}><Input type="number" data-testid="pr-other-cost" value={otherCost} onChange={(e) => setOtherCost(e.target.value)} /></Field>
            </div>
            <div className="mt-2 p-3 rounded-lg bg-[#3CD9B8]/10 border border-[#3CD9B8]/30 text-sm" data-testid="pr-total-cost">
              <span className="text-white/70">{t('tools.fiyat.total')}</span>{' '}
              <span className="font-bold text-[#3CD9B8]">{fmt(totalCost)}</span>
            </div>
          </Card>

          <Card>
            <h3 className="text-lg font-semibold mb-4">{t('tools.fiyat.section.targets')}</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label={t('tools.fiyat.margin')} hint={t('tools.fiyat.margin.hint')}><Input type="number" data-testid="pr-target-margin" value={targetMargin} onChange={(e) => setTargetMargin(e.target.value)} /></Field>
              <Field label={t('tools.fiyat.vat')}><Input type="number" data-testid="pr-vat" value={vatRate} onChange={(e) => setVatRate(e.target.value)} /></Field>
            </div>
          </Card>

          <Card>
            <h3 className="text-lg font-semibold mb-4">{t('tools.fiyat.section.platforms')}</h3>
            <div className="space-y-3">
              {platforms.map((p, i) => (
                <div key={p.id} className="flex items-center gap-3 text-sm">
                  <input type="checkbox" checked={p.enabled} onChange={(e) => updatePlatform(i, 'enabled', e.target.checked)} data-testid={`pr-platform-${p.id}`} className="w-4 h-4 accent-[#3CD9B8]" />
                  <span className="flex-1 font-medium">{p.label}</span>
                  <label className="text-xs text-white/50">{t('tools.fiyat.commission')}</label>
                  <input type="number" value={p.commission} onChange={(e) => updatePlatform(i, 'commission', e.target.value)} className="w-20 bg-white/5 border border-white/10 rounded px-2 py-1 text-sm" data-testid={`pr-commission-${p.id}`} />
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Results */}
        <div>
          <Card>
            <h3 className="text-lg font-semibold mb-4">{t('tools.fiyat.section.results')}</h3>
            <div className="space-y-3" data-testid="pr-results">
              {results.length === 0 ? (
                <p className="text-sm text-white/60">{t('tools.fiyat.no_platform')}</p>
              ) : results.map((r) => (
                <div key={r.id} className="p-4 rounded-lg bg-white/[0.04] border border-white/5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold">{r.label}</span>
                    <span className="text-xs text-white/50">{t('tools.fiyat.commission')} {r.commission}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <div className="text-xs text-white/50 mb-1">{t('tools.fiyat.sell_price')}</div>
                      <div className="text-lg font-bold text-[#3CD9B8]">{fmt(r.sellPrice)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-white/50 mb-1">{t('tools.fiyat.net_revenue')}</div>
                      <div className="text-sm font-semibold">{fmt(r.netRevenue)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-white/50 mb-1">{t('tools.fiyat.net_profit')}</div>
                      <div className="text-sm font-semibold text-emerald-400">{fmt(r.netProfit)}</div>
                    </div>
                  </div>
                  {r.realMargin != null && (
                    <div className="text-xs text-white/60 mt-2">{t('tools.fiyat.real_margin')} <span className="text-white font-semibold">%{r.realMargin.toFixed(1)}</span></div>
                  )}
                </div>
              ))}
            </div>
          </Card>
          <div className="mt-4 p-4 rounded-lg bg-white/[0.03] border border-white/10 text-xs text-white/60">
            {t('tools.fiyat.formula')} <code className="text-[#3CD9B8]">price = (cost × (1+margin) ÷ (1−commission)) × (1+VAT)</code>
          </div>
          <UpsellBanner />
        </div>
      </div>
    </ToolLayout>
  );
}
